import { zodResolver } from "@hookform/resolvers/zod";
import { useMemo } from "react";
import { Control, useForm, UseFormHandleSubmit, UseFormReturn } from "react-hook-form";
import { object, string } from "zod";

import { CODE_REGEX } from "src/constants/common";
import { useT } from "src/internationalization";
import { Rule } from "src/types/rule";

export type DetailsFormControl = {
  control: Control<DetailsFormData>;
  submit: UseFormHandleSubmit<DetailsFormData>;
  hasError: boolean;
  methods: UseFormReturn<DetailsFormData>;
};

export type DetailsFormData = {
  name: string;
  code: string;
  description: string;
};

export const useDetailsForm = ({ rules }: { rules: Rule[] }): DetailsFormControl => {
  const t = useT();

  const schema = useMemo(
    () =>
      object({
        name: string()
          .min(1, t("general.form.name.error.required"))
          .refine(
            (value) => {
              return !rules.some((rule) => rule.name === value);
            },
            {
              message: t("general.form.name.error.unique"),
            }
          ),
        code: string()
          .min(1, t("general.form.code.error.required"))
          .regex(CODE_REGEX, {
            message: t("general.form.code.error.invalid"),
          })
          .refine(
            (value) => {
              return !rules.some((rule) => rule.code === value);
            },
            {
              message: t("general.form.code.error.unique"),
            }
          ),
        description: string().min(1, t("general.form.description.error.required")),
      }),
    [t, rules]
  );

  const methods = useForm<DetailsFormData>({
    resolver: zodResolver(schema),
    defaultValues: {
      name: "",
      code: "",
      description: "",
    },
  });

  return {
    control: methods.control,
    submit: methods.handleSubmit,
    hasError: methods.formState.isSubmitted ? !methods.formState.isValid : false,
    methods,
  };
};
